"use client";

import { ReactNode, createContext, useContext } from "react";
import { ContentSearchCategory } from "./content/searchers/searchContent";
import { useYourLibrarySearch } from "./content/useYourLibrarySearch";
import { WorkSearchCategory } from "./types";
import { AgentSearchCategory, useAgentSearch } from "./useAgentSearch";
import { SearchState, useSearchState } from "./useSearchState";
import { useWorkSearch } from "./useWorksSearch";

export type SearchCategory = WorkSearchCategory | AgentSearchCategory | ContentSearchCategory;

export type SearchResult = {
  summary: ReactNode;
  categoryResults?: { id: SearchCategory; label: string; children: ReactNode }[];
  areSearchResultsLoading: boolean;
  areCategoriesLoading: boolean;
};

export interface SearchContextInterface {
  searchState?: SearchState;
  setSearchState: (change: SearchState) => void;
  workSearch?: SearchResult;
  agentSearch?: SearchResult;
  yourLibrarySearch?: SearchResult;
  areSearchResultsLoading?: boolean;
  areCategoriesLoading?: boolean;
}

export const SearchContext = createContext<SearchContextInterface>({ setSearchState: () => {} });

export const useSearchContext = () => useContext(SearchContext);

type SearchContextProviderProps = {
  children: ReactNode;
};

export const SearchContextProvider = ({ children }: SearchContextProviderProps) => {
  const [searchState, setSearchState] = useSearchState();
  const workSearch = useWorkSearch(searchState, setSearchState);
  const agentSearch = useAgentSearch(searchState, setSearchState);
  const yourLibrarySearch = useYourLibrarySearch(searchState, setSearchState);
  const areSearchResultsLoading =
    workSearch.areSearchResultsLoading ??
    agentSearch.areSearchResultsLoading ??
    yourLibrarySearch.areSearchResultsLoading;
  const areCategoriesLoading =
    workSearch.areCategoriesLoading || agentSearch.areCategoriesLoading || yourLibrarySearch.areCategoriesLoading;

  return (
    <SearchContext.Provider
      value={{
        searchState,
        setSearchState,
        workSearch,
        agentSearch,
        yourLibrarySearch,
        areSearchResultsLoading,
        areCategoriesLoading,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
